import {
  NetworkCommunity,
  CycloneCommunity,
} from "@voxmedia/duet-context/context/community-context";

import { EATER_COMMUNITY_ID, EATER_COMMUNITIES } from "./communities.eater";
import {
  SB_NATION_COMMUNITIES,
  SB_NATION_CYCLONE_COMMUNITY_FEED_QUERY_MAP,
  type CycloneCommunityFeedQueryConfig,
} from "./communities.sbnation";

export { EATER_CYCLONE_COMMUNITY_ID } from "./communities.eater";

export { SB_NATION_COMMUNITY_ID } from "./communities.sbnation";
export { EATER_COMMUNITY_ID } from "./communities.eater";

export const SB_NATION_NETWORK_ID = 1;
export const THE_VERGE_NETWORK_ID = 2;
export const EATER_NETWORK_ID = 6;

export const THE_VERGE_COMMUNITY_ID = 372;
export const VOX_CREATIVE_COMMUNITY_ID = 641;

export const THE_VERGE_CYCLONE_COMMUNITY_ID =
  "815a6122-a71c-47a0-a311-32f264c80c8a";

export const COMMUNITY_SHORTNAMES: Record<number, string> = {
  [THE_VERGE_COMMUNITY_ID]: "Verge",
  // Makes it so the short name for eater community is "Eater" only
  ...Object.entries(EATER_COMMUNITIES).reduce(
    (acc, [id]) => ({
      ...acc,
      [id]: "Eater",
    }),
    {},
  ),
};

type NetworkCommunityData = {
  [networkId: number]: {
    domain: string;
    isMultiCommunity?: boolean;
    isSubdomainFormatMultiCommunity?: boolean;
    communities: {
      [communityId: number]: NetworkCommunity;
    };
  };
};

export const NETWORK_COMMUNITY_DATA: NetworkCommunityData = {
  [THE_VERGE_NETWORK_ID]: {
    domain: "theverge.com",
    communities: {
      [THE_VERGE_COMMUNITY_ID]: {
        name: "The Verge",
        displayName: "The Verge",
        subdomain: null,
        domain: "theverge.com",
        wpSiteId: 2,
        isPrimaryCommunity: true,
        cycloneCommunities: {
          [THE_VERGE_CYCLONE_COMMUNITY_ID]: {
            displayName: "The Verge",
            isPrimaryCommunity: true,
            cycloneCommunityId: THE_VERGE_CYCLONE_COMMUNITY_ID,
            cycloneCommunityCacheId: 2,
          },
        },
      },
    },
  },
  [EATER_NETWORK_ID]: {
    domain: "eater.com",
    isMultiCommunity: true,
    isSubdomainFormatMultiCommunity: true,
    communities: EATER_COMMUNITIES,
  },
  [SB_NATION_NETWORK_ID]: {
    domain: "sbnation.com",
    isMultiCommunity: true,
    communities: SB_NATION_COMMUNITIES,
  },
};

/**
 * Return all cyclone community ids for a given network id.
 * @param networkId - The network ID to find the primary community ID for
 * @returns list of ids or null if no network is found
 */
export const getNetworkCycloneCommunityIds = (
  networkId: number,
): string[] | null =>
  NETWORK_COMMUNITY_DATA[networkId]
    ? Object.values(NETWORK_COMMUNITY_DATA[networkId].communities)
        .map((community) => Object.keys(community.cycloneCommunities ?? {}))
        .reduce((acc, ids) => acc.concat(...ids), [])
    : null;

export const getNetworkCommunityDataById = (
  networkId: number,
  communityId: number,
): NetworkCommunity | undefined =>
  NETWORK_COMMUNITY_DATA[networkId]?.communities[communityId];

/**
 * Gets a cyclone community by ID, with optional optimization for specific community
 * @param cycloneCommunityId - The cyclone community ID to find
 * @param communityId - Optional community ID to limit search scope
 * @returns The cyclone community data if found, undefined otherwise
 */
export const getCycloneCommunityById = (
  cycloneCommunityId: string,
  communityId?: number,
): CycloneCommunity | undefined => {
  if (communityId) {
    // Optimized path: search only in specified community
    const community = Object.values(NETWORK_COMMUNITY_DATA)
      .flatMap((network) => Object.values(network.communities))
      .find((community) => community.cycloneCommunities?.[cycloneCommunityId]);

    return community?.cycloneCommunities?.[cycloneCommunityId];
  }

  // General search: find in any community
  const community = Object.values(NETWORK_COMMUNITY_DATA)
    .flatMap((network) => Object.values(network.communities))
    .find((community) => community.cycloneCommunities?.[cycloneCommunityId]);

  // We found a community with the cyclone community id, return it
  return community?.cycloneCommunities?.[cycloneCommunityId];
};

/**
 * Returns the additional Honeypot feed-query context for a cyclone community.
 *
 * Supports the SB Nation mobile app feed query (it's only sending a cycloneCommunityId).
 * A cyclone community currently implies both the cyclone community content
 * and a paired Chorus community with an optional WordPress category filter.
 */
export const getCycloneCommunityFeedQueryConfig = (
  cycloneCommunityId: string,
): CycloneCommunityFeedQueryConfig | undefined =>
  SB_NATION_CYCLONE_COMMUNITY_FEED_QUERY_MAP[cycloneCommunityId];

/**
 * Gets the primary cyclone community ID for a given community ID.
 * Returns the cyclone community ID that has isPrimaryCommunity set to true.
 * @param communityId - The community ID to get the primary cyclone community ID for
 * @returns The primary cyclone community ID for the community
 * @throws Error if no community or primary cyclone community is found
 */
export const getCommunityPrimaryCycloneCommunityId = (
  communityId: number,
): string => {
  // Find the network that contains this community
  const network = Object.values(NETWORK_COMMUNITY_DATA).find(
    (network) => communityId in network.communities,
  );

  if (!network) {
    throw new Error(`No network found for community ID: ${communityId}`);
  }

  // Find the primary community
  const primaryCommunity = Object.values(network.communities).find(
    (community) => community.isPrimaryCommunity,
  );

  if (!primaryCommunity) {
    throw new Error(
      `No primary community found for network: ${network.domain}`,
    );
  }

  const community = network.communities[communityId];
  if (!community) {
    throw new Error(`No community found for community ID: ${communityId}`);
  }

  // Find the primary cyclone community for the primary community
  let primaryCycloneCommunity = primaryCommunity.cycloneCommunities
    ? Object.values(primaryCommunity.cycloneCommunities).find(
        (cycloneCommunity) => cycloneCommunity.isPrimaryCommunity,
      )
    : undefined;

  // If this subcommunity has cyclone communities, find the primary cyclone community
  if (community.cycloneCommunities) {
    // Find the primary cyclone community for this community
    primaryCycloneCommunity = Object.values(community.cycloneCommunities).find(
      (cycloneCommunity) => cycloneCommunity.isPrimaryCommunity,
    );
  }

  if (!primaryCycloneCommunity) {
    throw new Error(
      `No primary cyclone community found for community ${community.name} or for primary community ${primaryCommunity.name}`,
    );
  }

  return primaryCycloneCommunity.cycloneCommunityId;
};

export const getEaterCityCommunities = (): NetworkCommunity[] =>
  Object.entries(NETWORK_COMMUNITY_DATA[EATER_NETWORK_ID]!.communities)
    .filter(([id]) => parseInt(id) !== EATER_COMMUNITY_ID)
    .map(([, city]) => city);

/**
 * Gets the primary community ID for the given network ID.
 * Returns the community ID (key) that has isPrimaryCommunity set to true.
 * @param networkId - The network ID to find the primary community ID for
 * @returns The primary community ID for the network
 * @throws Error if no network or primary community is found
 */
export const getNetworkPrimaryCommunityId = (networkId: number): number => {
  const network = NETWORK_COMMUNITY_DATA[networkId];

  if (!network) {
    throw new Error(`No network found for network ID: ${networkId}`);
  }

  // Find the primary community ID in this network
  const primaryCommunityEntry = Object.entries(network.communities).find(
    ([, community]) => community.isPrimaryCommunity,
  );

  if (!primaryCommunityEntry) {
    throw new Error(
      `No primary community found for network: ${network.domain}`,
    );
  }

  return parseInt(primaryCommunityEntry[0]);
};
